// 이 앱으로 뽑은 그림 보여 주기: 로그인 그림판(천천히 바뀌는 대표 그림 3장)과 흐름 세 장(3D 기준 → 1차 무대 시안 → 2차 연출컷).
// 그림은 public/hero/ 에 있다(브랜딩/hero 에서 골라 줄인 시험 결과, 새로 생성한 그림 없음). AI가 그린 그림에는 늘 'AI 이미지'를 밝힌다.
import { icon } from "../icons";
import { h } from "../lib";

type Phase = "p1" | "p2";
type Slide = { file: string; phase: Phase; label: string; focus: string };

/**
 * 로그인 그림판. 첫 장만 바로 받고, 나머지는 첫 장이 뜬 뒤에 받는다.
 * SLIDES[0] 을 바꾸면 public/index.html 의 미리 받기(preload) 주소도 같이 바꾼다.
 * 로그인 확인 틀(브랜딩/tools/login-harness/entry.ts)의 SLIDES 도 같은 순서·이름으로 맞춘다.
 */
const SLIDES: Slide[] = [
  { file: "hero-01", phase: "p1", label: "1차 무대 시안", focus: "50% 55%" },
  { file: "hero-06", phase: "p2", label: "2차 연출컷", focus: "50% 50%" },
  // hero-04 는 확정 무대를 다른 방향에서 본 그림(8788 DB kind=stage-angle)이라 1차다
  { file: "hero-04", phase: "p1", label: "1차 무대 시안", focus: "50% 45%" },
];
const SHOW_MS = 7000;
const FIRST_WAIT_MS = 2500;

/**
 * 그림이 실제로 그려지는 폭(sizes). 그림판은 세로로 길고 그림은 object-fit: cover 라서,
 * 칸 폭보다 '높이 × 16/9'가 더 넓을 때가 많다. 그 폭으로 적어야 브라우저가 충분히 큰 판을 고른다.
 *  - 960px 이하: 그림 띠 높이 clamp(220px, 34vh, 300px) → max(100vw, 그 높이 × 1.778)
 *  - 그 위: 그림판 폭 100vw - max(400px, 34vw), 높이 100vh → max(칸 폭, 177.7vh)
 * public/index.html 미리 받기의 imagesizes 와 글자 하나까지 같아야 한다(다르면 그림을 두 번 받는다).
 */
export const HERO_SIZES = "(max-width: 960px) max(100vw, clamp(391px, 60.4vh, 533px)), max(calc(100vw - max(400px, 34vw)), 177.7vh)";
/** 960w·1920w·2560w 세 판(원본 2752×1536 을 줄이기만 함). index.html 미리 받기의 imagesrcset 과 같아야 한다 */
const srcset = (file: string) => `/hero/${file}-sm.jpg 960w, /hero/${file}.jpg 1920w, /hero/${file}-xl.jpg 2560w`;
// 'AI 이미지'는 줄이 꺾여도 떨어지지 않게 붙인다(줄바꿈 없는 띄어쓰기)
const captionText = (s: Slide) => `STAGE X로 뽑은 ${s.label} · AI\u00a0이미지`;

export type HeroArt = {
  figure: HTMLElement;
  caption: HTMLElement;
  /** 넘김을 멈춘다(입력칸에 들어갈 때). 멈춤 단추와 같은 상태다 */
  pause: () => void;
  /** 아주 멈춘다(로그인이 끝났거나 로그인 화면을 그리지 못했을 때). 남은 그림을 받지 않고 타이머를 지운다 */
  stop: () => void;
};

/**
 * 그림판과 설명 칩을 만든다. 그림은 꾸밈이라 화면 읽기 프로그램에는 숨기고(alt=""),
 * 칩에는 'AI 이미지'를 밝혀 실제 현장 사진으로 오해되지 않게 한다.
 * 움직임 줄이기를 켰거나 데이터 절약 모드면 첫 장만 가만히 둔다.
 * 넘기는 동안에는 칩 앞에 멈춤 단추를 두고, 한 바퀴(3장)를 돌아 첫 장으로 오면 저절로 멈춘다(WCAG 2.2.2).
 */
export function heroArt(): HeroArt {
  const imgs = SLIDES.map((s, i) =>
    h("img", {
      class: i === 0 ? "on" : null,
      alt: "",
      decoding: "async",
      sizes: HERO_SIZES,
      // 첫 장은 바로 받는다(로그인 화면이 열리기 전에 받기 시작한다). 나머지는 data-* 에 두었다가 넘기기 시작할 때 받는다
      ...(i === 0 ? { fetchpriority: "high", srcset: srcset(s.file), src: `/hero/${s.file}.jpg` } : { "data-srcset": srcset(s.file), "data-src": `/hero/${s.file}.jpg` }),
      style: { objectPosition: s.focus },
    }),
  );
  const figure = h("figure", { class: "art", "aria-hidden": "true" }, imgs);
  const label = h("span", null, captionText(SLIDES[0]));
  const caption = h("p", { class: "art-caption", "data-phase": SLIDES[0].phase }, h("span", { class: "dot", "aria-hidden": "true" }), label);
  const first = imgs[0];

  let stopped = false;
  let paused = false;
  let ready = false; // 첫 장이 뜨고 FIRST_WAIT_MS 가 지났다
  let loaded = false; // 나머지 그림을 받기 시작했다
  let cur = 0;
  let timer = 0;
  let wait = 0;
  let toggle: HTMLButtonElement | null = null;

  const clearTimer = () => {
    window.clearInterval(timer);
    timer = 0;
  };
  const stop = () => {
    stopped = true;
    window.clearTimeout(wait);
    clearTimer();
    first.removeEventListener("load", afterFirst);
    if (toggle) toggle.hidden = true;
  };
  const afterFirst = () => {
    if (!stopped) wait = window.setTimeout(later, FIRST_WAIT_MS);
  };

  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  const saveData = Boolean((navigator as any).connection?.saveData);
  if (reduce?.matches || saveData) return { figure, caption, pause: () => {}, stop };

  const loadRest = () => {
    if (loaded) return;
    loaded = true;
    for (const img of imgs.slice(1)) {
      img.srcset = img.dataset.srcset || "";
      img.src = img.dataset.src || "";
    }
  };
  const play = () => {
    if (stopped || paused || !ready || timer) return;
    // 화면에 붙어 있을 때만 남은 그림을 받는다(아직 안 붙었으면 첫 차례에 tick 이 받는다)
    if (figure.isConnected) loadRest();
    timer = window.setInterval(tick, SHOW_MS);
  };
  const setPaused = (v: boolean) => {
    paused = v;
    btn.setAttribute("aria-pressed", String(v));
    btn.replaceChildren(icon(v ? "play" : "pause", 14));
    if (v) clearTimer();
    else play();
  };
  const show = (n: number) => {
    imgs[cur].classList.remove("on");
    imgs[n].classList.add("on");
    cur = n;
    caption.dataset.phase = SLIDES[n].phase;
    label.textContent = captionText(SLIDES[n]);
  };
  const tick = () => {
    // 로그인 화면이 화면에서 떨어졌으면(로그인 끝, 다른 화면으로 감) 아주 멈춘다
    if (!figure.isConnected) return stop();
    loadRest();
    if (document.hidden || reduce?.matches) return;
    const next = (cur + 1) % imgs.length;
    const img = imgs[next];
    if (!img.complete || !img.naturalWidth) return; // 아직 못 받았으면 다음 차례에
    show(next);
    // 한 바퀴 돌아 첫 장으로 오면 멈춘다. 단추로 다시 돌릴 수 있다
    if (next === 0) setPaused(true);
  };
  const later = () => {
    if (stopped) return;
    ready = true;
    play();
  };

  // 멈춤 단추: 칩 맨 앞(키보드로 닿게 aria-hidden 그림판 밖, 칩 안에 둔다).
  // 칩 글자(label)는 늘 마지막 자식으로 둔다(로그인 확인 틀이 lastElementChild 로 글자를 바꾼다)
  const btn = h("button", { class: "art-pause", type: "button", "aria-pressed": "false", "aria-label": "그림 넘김 멈추기" }, icon("pause", 14));
  btn.addEventListener("click", () => setPaused(!paused));
  toggle = btn;
  caption.prepend(btn);

  if (first.complete && first.naturalWidth) afterFirst();
  else first.addEventListener("load", afterFirst, { once: true });
  return { figure, caption, pause: () => setPaused(true), stop };
}

/** 흐름 세 장. 같은 호숫가 시험 프로젝트에서 뽑은 기준 그림 → 1차 → 2차 (alt 는 브랜딩/hero/manifest.json 그대로) */
const TRIO: { file: string; tag: string; phase?: Phase; alt: string }[] = [
  { file: "trio-1", tag: "3D 기준", alt: "3D로 잡은 호숫가 장소: 호수, 무대 자리(점선), 관객 자리만 표시한 기준 그림" },
  { file: "trio-2", tag: "1차 · 무대 시안", phase: "p1", alt: "같은 자리에 세운 1차 무대 시안: 흰 각진 날개 구조와 런웨이, 원형 돌출 무대" },
  { file: "trio-3", tag: "2차 · 연출컷", phase: "p2", alt: "2차 연출컷: 같은 무대에서 불꽃과 불기둥이 터지는 밤 공연 장면" },
];

/**
 * 흐름 세 장이 그려지는 폭: 로그인 오른쪽 칸 약 104~120px, 빈 프로젝트 목록(compact, 최대 440px 칸) 약 136px,
 * 960px 이하 로그인은 한 칸 전체를 셋으로 나눠 최대 약 291px(≈ 31vw).
 * 작은 판(trio-N-sm.jpg, 640×360)은 원본을 줄이기만 했다. 줄어드는 효과는 주로 trio-2·trio-3 에서 난다(trio-1 은 원래 14KB).
 */
const trioSizes = (compact?: boolean) => (compact ? "136px" : "(max-width: 960px) 31vw, 120px");

export function flowTrio(o: { title: string; compact?: boolean }): HTMLElement {
  return h(
    "figure",
    { class: `flow${o.compact ? " compact" : ""}` },
    h("figcaption", null, h("span", null, o.title), h("span", { class: "ai-note" }, "1차·2차는 AI 이미지")),
    h(
      "ol",
      null,
      TRIO.map((t) =>
        h(
          "li",
          { class: t.phase || null },
          // loading·sizes·srcset 을 src 보다 먼저 단다(먼저 단 src 로 큰 판을 바로 받는 브라우저가 있다). width/height 는 칸 비율용
          h("img", {
            alt: t.alt,
            width: 1200,
            height: 675,
            loading: "lazy",
            decoding: "async",
            sizes: trioSizes(o.compact),
            srcset: `/hero/${t.file}-sm.jpg 640w, /hero/${t.file}.jpg 1200w`,
            src: `/hero/${t.file}.jpg`,
          }),
          h("span", { class: "tag" }, t.tag),
        ),
      ),
    ),
  );
}
